/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useCallback, useEffect, useMemo } from "react";
import FormGroup from "../../ui/form/FormGroup";
import FormError from "../../ui/form/FormError";
import Checkbox from "../../ui/form/Checkbox";
import TermsCheckbox from "./TermsCheckbox";
import T from "../../ui/Translation";
import { CLIENT_REGISTER_POLICY_FIELDS_KEYS } from "../../../constants/constants";
import { useBooking } from "../../../hooks/useBooking";
import { useCompany } from "../../../lib/react-query/useCompany";
import { useSimplybookTerms } from "../../../lib/react-query/useSimplybookTerms";
import { useClientCancellationTerms } from "../../../lib/react-query/useClientCancellationTerms";
import { useClientUserTerms } from "../../../lib/react-query/useClientUserTerms";
import { useClientPrivacyPolicy } from "../../../lib/react-query/useClientPrivacyPolicy";

const ClientTermsCheckboxes = ({ form, terms }) => {
  const {
    control,
    setValue,
    formState: { errors },
    getValues,
  } = form;
  const { updateBooking } = useBooking();
  const { data: company } = useCompany();

  const termKeys = useMemo(() => [
    CLIENT_REGISTER_POLICY_FIELDS_KEYS.SIMPLYBOOK_TERMS,
    CLIENT_REGISTER_POLICY_FIELDS_KEYS.USER_TERMS,
    CLIENT_REGISTER_POLICY_FIELDS_KEYS.CANCELLATION_TERMS,
    CLIENT_REGISTER_POLICY_FIELDS_KEYS.PROMOTION_LETTERS,
    CLIENT_REGISTER_POLICY_FIELDS_KEYS.PRIVACY_POLICY,
  ], []);

  const generateTermsObject = useCallback(() => {
    return termKeys.reduce((acc, key) => {
      const acceptedKey = `accepted_${key}`;
      const enabledKey = `enabled_${key}`;

      if (terms[enabledKey]) {
        acc[key] = terms[acceptedKey] || getValues(key);
      }

      return acc;
    }, {});
  }, [terms, getValues, termKeys]);

  // Memoize the terms object to prevent unnecessary updates
  const currentTermsObject = useMemo(() => generateTermsObject(), [generateTermsObject]);

  // Update booking only when terms actually change
  useEffect(() => {
    updateBooking({ terms: currentTermsObject });
  }, [currentTermsObject, updateBooking]);

  const updateTermsValues = useCallback(() => {
    const currentTerms = generateTermsObject();

    Object.keys(currentTerms).forEach((key) => {
      if (key.startsWith("accepted_") || key.startsWith("enabled_")) {
        currentTerms[key] = terms?.[key] || false;
      } else {
        currentTerms[key] = terms?.[`accepted_${key}`] || (terms?.[`enabled_${key}`] && getValues(key)) || false;
      }
    });

    updateBooking({ terms: currentTerms });
  }, [generateTermsObject, terms, getValues, updateBooking]);

  const handleAgree = useCallback(
    (fieldName) => {
      setValue(fieldName, true);
      updateTermsValues();
    },
    [setValue, updateTermsValues]
  );

  const handleCheckboxChange = useCallback(
    () => {
      updateTermsValues();
    },
    [updateTermsValues]
  );

  return (
    <>
      {!terms?.accepted_simplybook_terms && terms?.enabled_simplybook_terms && (
        <FormGroup>
          <TermsCheckbox
            control={control}
            name={CLIENT_REGISTER_POLICY_FIELDS_KEYS.SIMPLYBOOK_TERMS}
            onAgree={() => handleAgree(CLIENT_REGISTER_POLICY_FIELDS_KEYS.SIMPLYBOOK_TERMS)}
            required={true}
            onChange={handleCheckboxChange}
            queryHook={useSimplybookTerms}
            label={<T project="SimplyBook.me">client_agree_with_terms_and_conditions</T>}
            title="simplybook_terms"
          />
          {errors.simplybook_terms && <FormError>{errors.simplybook_terms.message}</FormError>}
        </FormGroup>
      )}
      {!terms?.accepted_cancellation_terms && terms?.enabled_cancellation_terms && (
        <FormGroup>
          <TermsCheckbox
            control={control}
            name={CLIENT_REGISTER_POLICY_FIELDS_KEYS.CANCELLATION_TERMS}
            onAgree={() => handleAgree(CLIENT_REGISTER_POLICY_FIELDS_KEYS.CANCELLATION_TERMS)}
            required={true}
            onChange={handleCheckboxChange}
            queryHook={useClientCancellationTerms}
            label={terms?.cancellation_terms_label}
            title="cancellation_terms"
          />
          {errors.cancellation_terms && <FormError>{errors.cancellation_terms.message}</FormError>}
        </FormGroup>
      )}
      {!terms?.accepted_user_terms && terms?.enabled_user_terms && (
        <FormGroup>
          <TermsCheckbox
            control={control}
            name={CLIENT_REGISTER_POLICY_FIELDS_KEYS.USER_TERMS}
            onAgree={() => handleAgree(CLIENT_REGISTER_POLICY_FIELDS_KEYS.USER_TERMS)}
            onChange={handleCheckboxChange}
            required={true}
            queryHook={useClientUserTerms}
            label={<T company={company.name}>client_agree_with_user_terms_and_conditions</T>}
            title="user_terms"
          />
          {errors.user_terms && <FormError>{errors.user_terms.message}</FormError>}
        </FormGroup>
      )}
      {!terms?.accepted_privacy_policy && terms?.enabled_privacy_policy && (
        <FormGroup>
          <TermsCheckbox
            control={control}
            name={CLIENT_REGISTER_POLICY_FIELDS_KEYS.PRIVACY_POLICY}
            onAgree={() => handleAgree(CLIENT_REGISTER_POLICY_FIELDS_KEYS.PRIVACY_POLICY)}
            onChange={handleCheckboxChange}
            required={true}
            queryHook={useClientPrivacyPolicy}
            label={<T company={company.name}>client_agree_with_privacy_policy</T>}
            title={<T company_name={company.name}>%company_name%_privacy_policy_title</T>}
          />
          {errors.privacy_policy && <FormError>{errors.privacy_policy.message}</FormError>}
        </FormGroup>
      )}
      {!terms?.accepted_promotion_letters && terms?.enabled_promotion_letters && (
        <FormGroup>
          <Checkbox
            control={control}
            name={CLIENT_REGISTER_POLICY_FIELDS_KEYS.PROMOTION_LETTERS}
            onChange={handleCheckboxChange}
          >
            client_agree_with_receive_promo_letters
          </Checkbox>
          {errors.promotion_letters && <FormError>{errors.promotion_letters.message}</FormError>}
        </FormGroup>
      )}
    </>
  );
};

export default ClientTermsCheckboxes;
